import { Container, rem, Stepper } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

import { Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';

import { StoreInfoHeading } from '@apple/features/store-events/components/store-info-heading';

export const Route = createFileRoute('/_authed/store-events/_steps')({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const matches = useMatches();
	const { t } = useTranslation('store-event');
	const vertical = useMediaQuery('(max-width: lg)');
	const currentStepIndex = useMemo(() => {
		const lastMatch = matches.at(-1);
		switch (lastMatch?.fullPath) {
			case '/store-events/$storeEventId/details':
				return 0;
			case '/store-events/$storeEventId/categories':
				return 1;
			case '/store-events/$storeEventId/products':
				return 2;
			default:
				return 0;
		}
	}, [matches]);
	const storeEventId = useMemo(() => {
		const lastMatch = matches.at(-1);
		const params = lastMatch?.params as Record<string, string>;
		return params.storeEventId;
	}, [matches]);

	const handleGotoStep = useCallback(
		(step: number) => {
			if (!storeEventId) {
				navigate({
					to: '/store-events/new',
				});
				return;
			}

			switch (step) {
				case 0:
					navigate({
						to: '/store-events/$storeEventId/details',
						params: {
							storeEventId,
						},
					});
					break;
				case 1:
					navigate({
						to: '/store-events/$storeEventId/categories',
						params: {
							storeEventId,
						},
					});
					break;
				case 2:
					navigate({
						to: '/store-events/$storeEventId/products',
						params: {
							storeEventId,
						},
					});
					break;
				default:
					navigate({
						to: '/store-events/$storeEventId/details',
						params: {
							storeEventId,
						},
					});
					break;
			}
		},
		[storeEventId, navigate],
	);

	const handleDetailsClick = useCallback(
		() =>
			storeEventId &&
			navigate({
				to: '/store-events/$storeEventId/details',
				params: {
					storeEventId,
				},
			}),
		[navigate, storeEventId],
	);

	const handleProductsClick = useCallback(
		() =>
			storeEventId &&
			navigate({
				to: '/store-events/$storeEventId/products',
				params: {
					storeEventId,
				},
				search: {
					onlyBackOrderedItem: true,
				},
			}),
		[navigate, storeEventId],
	);

	return (
		<Container fluid mt={'var(--app-shell-header-height)'}>
			{storeEventId && (
				<StoreInfoHeading
					storeEventId={storeEventId}
					onDetailsClick={handleDetailsClick}
					onProductsClick={handleProductsClick}
				/>
			)}
			<Stepper
				size='sm'
				completedIcon={<Success c='white' />}
				active={currentStepIndex}
				onStepClick={handleGotoStep}
				allowNextStepsSelect={true}
				orientation={vertical ? 'vertical' : 'horizontal'}
				styles={{
					steps: {
						paddingLeft: rem(32),
						paddingRight: rem(32),
						paddingTop: rem(32),
					},
				}}
			>
				<Stepper.Step label={t('details.title')} description={t('details.description')}>
					<Outlet />
				</Stepper.Step>

				<Stepper.Step
					label={t('categories.title')}
					description={t('categories.description')}
				>
					<Outlet />
				</Stepper.Step>

				<Stepper.Step label={t('products.title')} description={t('products.description')}>
					<Outlet />
				</Stepper.Step>

				<Stepper.Completed>
					<Outlet />
				</Stepper.Completed>
			</Stepper>
		</Container>
	);
}
